import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import api, { resolveImageUrl } from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Shield, Loader2, X, TrendingUp } from 'lucide-react';
import TipTapEditor from '@/components/TipTapEditor';

const AdminPage = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { settings, updateSettings, refreshSettings } = useSettings();
  const navigate = useNavigate();

  const [policies, setPolicies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [customPages, setCustomPages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [bannerSlides, setBannerSlides] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [policyDialog, setPolicyDialog] = useState({ open: false, mode: 'create', data: null });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: 'create', data: null });
  const [providerDialog, setProviderDialog] = useState({ open: false, mode: 'create', data: null });
  const [pageDialog, setPageDialog] = useState({ open: false, mode: 'create', data: null });
  const [slideDialog, setSlideDialog] = useState({ open: false, mode: 'create', data: null });
  const [userDialog, setUserDialog] = useState({ open: false, mode: 'edit', data: null });
  const [submitting, setSubmitting] = useState(false);
  const [pageEditorContent, setPageEditorContent] = useState('');

  // Settings state
  const [companyName, setCompanyName] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Page Builder state
  const [pageContent, setPageContent] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_cta_text: '',
    hero_image_url: '',
    stats: [],
    footer_description: '',
    footer_email: '',
    footer_phone: '',
    footer_columns: [],
    social_media: [],
    company_full_name: '',
    irdai_license: '',
    broker_type: '',
    license_valid_till: '',
    principal_officer: '',
    cin: '',
    registered_office: '',
    corporate_office: '',
    disclaimer_text: '',
    footer_disclaimer_full: '',
    ndnc_authorization: ''
  });

  useEffect(() => {
    if (!authLoading && !isAdmin()) {
      toast.error('Admin access required');
      navigate('/login');
    } else if (!authLoading && isAdmin()) {
      fetchAllData();
    }
  }, [authLoading, user]);

  // Initialize company name from settings
  useEffect(() => {
    if (settings) {
      setCompanyName(settings.company_name);
      setPageContent({
        hero_title: settings.hero_title || '',
        hero_subtitle: settings.hero_subtitle || '',
        hero_cta_text: settings.hero_cta_text || '',
        hero_image_url: settings.hero_image_url || '',
        stats: settings.stats || [],
        footer_description: settings.footer_description || '',
        footer_email: settings.footer_email || '',
        footer_phone: settings.footer_phone || '',
        footer_columns: settings.footer_columns || [],
        social_media: settings.social_media || [],
        company_full_name: settings.company_full_name || '',
        irdai_license: settings.irdai_license || '',
        broker_type: settings.broker_type || '',
        license_valid_till: settings.license_valid_till || '',
        principal_officer: settings.principal_officer || '',
        cin: settings.cin || '',
        registered_office: settings.registered_office || '',
        corporate_office: settings.corporate_office || '',
        disclaimer_text: settings.disclaimer_text || '',
        footer_disclaimer_full: settings.footer_disclaimer_full || '',
        ndnc_authorization: settings.ndnc_authorization || ''
      });
    }
  }, [settings]);

  // Initialize page editor content when dialog opens
  useEffect(() => {
    if (pageDialog.open && pageDialog.data) {
      setPageEditorContent(pageDialog.data.content || '');
    } else if (pageDialog.open && !pageDialog.data) {
      setPageEditorContent('');
    }
  }, [pageDialog.open, pageDialog.data]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [policiesRes, categoriesRes, providersRes, pagesRes, leadsRes, slidesRes, usersRes] = await Promise.all([
        api.get('/policies'),
        api.get('/categories'),
        api.get('/providers'),
        api.get('/pages'),
        api.get('/lead-enquiries'),
        api.get('/banner-slides', { params: { active_only: false } }),
        api.get('/users')
      ]);
      setPolicies(policiesRes.data);
      setCategories(categoriesRes.data);
      setProviders(providersRes.data);
      setCustomPages(pagesRes.data);
      setLeads(leadsRes.data);
      setBannerSlides(slidesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Policy CRUD
  const handlePolicySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      provider_id: formData.get('provider_id'),
      category_id: formData.get('category_id'),
      premium_yearly: parseFloat(formData.get('premium_yearly')),
      coverage_amount: parseFloat(formData.get('coverage_amount')),
      features: {
        cashless: formData.get('cashless') === 'on',
        pre_existing: formData.get('pre_existing') === 'on',
        network_size: formData.get('network_size') ? parseInt(formData.get('network_size')) : null,
        claim_settlement_ratio: formData.get('claim_settlement_ratio') ? parseFloat(formData.get('claim_settlement_ratio')) : null,
        waiting_period_days: formData.get('waiting_period_days') ? parseInt(formData.get('waiting_period_days')) : null
      },
      summary: formData.get('summary'),
      key_benefits: formData.get('key_benefits') ? formData.get('key_benefits').split('\n').filter(b => b.trim()) : [],
      exclusions: formData.get('exclusions') ? formData.get('exclusions').split('\n').filter(e => e.trim()) : [],
      terms: formData.get('terms') || null,
      is_popular: formData.get('is_popular') === 'on'
    };

    try {
      if (policyDialog.mode === 'create') {
        await api.post('/policies', data);
        toast.success('Policy created successfully');
      } else {
        await api.put(`/policies/${policyDialog.data.id}`, data);
        toast.success('Policy updated successfully');
      }
      setPolicyDialog({ open: false, mode: 'create', data: null });
      fetchAllData();
    } catch (error) {
      console.error('Failed to save policy:', error);
      toast.error(error.response?.data?.detail || 'Failed to save policy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePolicy = async (id) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    
    try {
      await api.delete(`/policies/${id}`);
      toast.success('Policy deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Failed to delete policy:', error);
      toast.error('Failed to delete policy');
    }
  };

  // Category CRUD
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      type: formData.get('type'),
      description: formData.get('description') || null
    };

    try {
      if (categoryDialog.mode === 'create') {
        await api.post('/categories', data);
        toast.success('Category created successfully');
      } else {
        await api.put(`/categories/${categoryDialog.data.id}`, data);
        toast.success('Category updated successfully');
      }
      setCategoryDialog({ open: false, mode: 'create', data: null });
      fetchAllData();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Category deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Failed to delete category');
    }
  };

  // Provider CRUD
  const handleProviderSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      logo_url: formData.get('logo_url') || null,
      description: formData.get('description') || null
    };

    try {
      if (providerDialog.mode === 'create') {
        await api.post('/providers', data);
        toast.success('Provider created successfully');
      } else {
        await api.put(`/providers/${providerDialog.data.id}`, data);
        toast.success('Provider updated successfully');
      }
      setProviderDialog({ open: false, mode: 'create', data: null });
      fetchAllData();
    } catch (error) {
      console.error('Failed to save provider:', error);
      toast.error(error.response?.data?.detail || 'Failed to save provider');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProvider = async (id) => {
    if (!window.confirm('Are you sure you want to delete this provider?')) return;
    
    try {
      await api.delete(`/providers/${id}`);
      toast.success('Provider deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Failed to delete provider:', error);
      toast.error('Failed to delete provider');
    }
  };

  // Custom Page CRUD
  const handlePageSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const formData = new FormData(e.target);
    const data = {
      url_slug: formData.get('url_slug'),
      title: formData.get('title'),
      content: pageEditorContent, // Use TipTap editor content
      meta_description: formData.get('meta_description') || null,
      meta_keywords: formData.get('meta_keywords') || null,
      og_title: formData.get('og_title') || null,
      og_description: formData.get('og_description') || null,
      og_image: formData.get('og_image') || null,
      canonical_url: formData.get('canonical_url') || null,
      robots: formData.get('robots') || 'index, follow',
      is_published: formData.get('is_published') === 'on'
    };

    try {
      if (pageDialog.mode === 'create') {
        await api.post('/pages', data);
        toast.success('Page created successfully');
      } else {
        await api.put(`/pages/${pageDialog.data.id}`, data);
        toast.success('Page updated successfully');
      }
      setPageDialog({ open: false, mode: 'create', data: null });
      setPageEditorContent('');
      fetchAllData();
    } catch (error) {
      console.error('Failed to save page:', error);
      toast.error(error.response?.data?.detail || 'Failed to save page');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    
    try {
      await api.delete(`/pages/${id}`);
      toast.success('Page deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Failed to delete page:', error);
      toast.error('Failed to delete page');
    }
  };

  // Lead status update
  const handleUpdateLeadStatus = async (leadId, newStatus) => {
    try {
      await api.put(`/lead-enquiries/${leadId}`, { status: newStatus });
      toast.success('Lead status updated');
      fetchAllData();
    } catch (error) {
      console.error('Failed to update lead status:', error);
      toast.error('Failed to update status');
    }
  };

  // Banner slide management
  const handleAddBannerSlide = async (imageUrl, title, subtitle, ctaText, ctaLink) => {
    try {
      const order = bannerSlides.length + 1;
      await api.post('/banner-slides', {
        title,
        subtitle: subtitle || null,
        image_url: imageUrl,
        cta_text: ctaText || null,
        cta_link: ctaLink || null,
        order,
        is_active: true
      });
      toast.success('Banner slide added successfully');
      fetchAllData();
    } catch (error) {
      console.error('Failed to add banner slide:', error);
      toast.error('Failed to add slide');
    }
  };

  const handleDeleteBannerSlide = async (slideId) => {
    if (!window.confirm('Are you sure you want to delete this banner slide?')) return;
    
    try {
      await api.delete(`/banner-slides/${slideId}`);
      toast.success('Banner slide deleted');
      fetchAllData();
    } catch (error) {
      console.error('Failed to delete banner slide:', error);
      toast.error('Failed to delete slide');
    }
  };

  const handleToggleSlideActive = async (slideId, currentStatus) => {
    try {
      await api.put(`/banner-slides/${slideId}`, { is_active: !currentStatus });
      toast.success(`Slide ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchAllData();
    } catch (error) {
      console.error('Failed to toggle slide:', error);
      toast.error('Failed to update slide');
    }
  };

  // User management
  const handleToggleUserBlock = async (userId, currentStatus) => {
    try {
      await api.put(`/users/${userId}`, { is_blocked: !currentStatus });
      toast.success(`User ${!currentStatus ? 'blocked' : 'unblocked'}`);
      fetchAllData();
    } catch (error) {
      console.error('Failed to toggle user block:', error);
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const formData = new FormData(e.target);
    
    try {
      if (userDialog.mode === 'create') {
        // Create new user
        const data = {
          phone: formData.get('phone'),
          name: formData.get('name'),
          email: formData.get('email'),
          password: formData.get('password'),
          role: formData.get('role')
        };
        
        await api.post('/users', data);
        toast.success('User created successfully');
      } else {
        // Update existing user
        const data = {
          name: formData.get('name') || null,
          email: formData.get('email') || null,
          role: formData.get('role'),
          new_password: formData.get('new_password') || null
        };
        
        await api.put(`/users/${userDialog.data.id}`, data);
        toast.success('User updated successfully');
      }
      
      setUserDialog({ open: false, mode: 'edit', data: null });
      fetchAllData();
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSlideSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const formData = new FormData(e.target);
    const data = {
      title: formData.get('title'),
      subtitle: formData.get('subtitle') || null,
      image_url: formData.get('image_url'),
      cta_text: formData.get('cta_text') || null,
      cta_link: formData.get('cta_link') || null,
      order: slideDialog.data?.order || (bannerSlides.length + 1),
      is_active: formData.get('is_active') === 'on'
    };

    try {
      if (slideDialog.mode === 'create') {
        await api.post('/banner-slides', data);
        toast.success('Banner slide created');
      } else {
        await api.put(`/banner-slides/${slideDialog.data.id}`, data);
        toast.success('Banner slide updated');
      }
      setSlideDialog({ open: false, mode: 'create', data: null });
      fetchAllData();
    } catch (error) {
      console.error('Failed to save slide:', error);
      toast.error('Failed to save slide');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCompanyName = async () => {
    if (!companyName.trim()) {
      toast.error('Company name cannot be empty');
      return;
    }

    setSubmitting(true);
    try {
      await updateSettings({ company_name: companyName });
      toast.success('Company name updated successfully!');
    } catch (error) {
      console.error('Failed to update company name:', error);
      toast.error('Failed to update company name');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePageContent = async () => {
    setSubmitting(true);
    try {
      const result = await updateSettings(pageContent);
      toast.success('Page content updated successfully!', {
        description: 'Changes are live on your website'
      });
      // Force refresh settings to update homepage
      await refreshSettings();
      // Small delay to ensure state update
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Failed to update page content:', error);
      toast.error('Failed to update page content');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatChange = (index, field, value) => {
    const newStats = [...pageContent.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setPageContent(prev => ({ ...prev, stats: newStats }));
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image file must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResponse = await api.post('/upload-document', formData);
      // Store only the relative path - this works across all environments
      const imageUrl = uploadResponse.data.file_url;
      
      setPageContent(prev => ({ ...prev, hero_image_url: imageUrl }));
      toast.success('Hero image uploaded! Click "Save Changes" to apply');
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleAddFooterColumn = () => {
    setPageContent(prev => ({
      ...prev,
      footer_columns: [...prev.footer_columns, { title: '', links: [{ text: '', url: '' }] }]
    }));
  };

  const handleRemoveFooterColumn = (index) => {
    setPageContent(prev => ({
      ...prev,
      footer_columns: prev.footer_columns.filter((_, i) => i !== index)
    }));
  };

  const handleFooterColumnChange = (index, field, value) => {
    const newColumns = [...pageContent.footer_columns];
    newColumns[index] = { ...newColumns[index], [field]: value };
    setPageContent(prev => ({ ...prev, footer_columns: newColumns }));
  };

  const handleAddLink = (columnIndex) => {
    const newColumns = [...pageContent.footer_columns];
    newColumns[columnIndex].links = [...(newColumns[columnIndex].links || []), { text: '', url: '' }];
    setPageContent(prev => ({ ...prev, footer_columns: newColumns }));
  };

  const handleRemoveLink = (columnIndex, linkIndex) => {
    const newColumns = [...pageContent.footer_columns];
    newColumns[columnIndex].links = newColumns[columnIndex].links.filter((_, i) => i !== linkIndex);
    setPageContent(prev => ({ ...prev, footer_columns: newColumns }));
  };

  const handleLinkChange = (columnIndex, linkIndex, field, value) => {
    const newColumns = [...pageContent.footer_columns];
    newColumns[columnIndex].links[linkIndex] = {
      ...newColumns[columnIndex].links[linkIndex],
      [field]: value
    };
    setPageContent(prev => ({ ...prev, footer_columns: newColumns }));
  };

  const handleAddSocialMedia = () => {
    setPageContent(prev => ({
      ...prev,
      social_media: [...prev.social_media, { platform: 'Facebook', url: '' }]
    }));
  };

  const handleRemoveSocialMedia = (index) => {
    setPageContent(prev => ({
      ...prev,
      social_media: prev.social_media.filter((_, i) => i !== index)
    }));
  };

  const handleSocialMediaChange = (index, field, value) => {
    const newSocial = [...pageContent.social_media];
    newSocial[index] = { ...newSocial[index], [field]: value };
    setPageContent(prev => ({ ...prev, social_media: newSocial }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-secondary-50 p-8">
        <Skeleton className="h-12 w-1/4 mb-8" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50" data-testid="admin-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Shield className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold font-heading text-secondary-900">Admin Panel</h1>
              <p className="text-base text-secondary-600">Manage policies, categories, and providers</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="policies" className="w-full">
          <TabsList className="grid w-full grid-cols-8 mb-8" data-testid="admin-tabs">
            <TabsTrigger value="policies">Policies ({policies.length})</TabsTrigger>
            <TabsTrigger value="categories">Categories ({categories.length})</TabsTrigger>
            <TabsTrigger value="providers">Providers ({providers.length})</TabsTrigger>
            <TabsTrigger value="pages">Pages</TabsTrigger>
            <TabsTrigger value="leads">Leads</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="pagebuilder">Builder</TabsTrigger>
          </TabsList>

          {/* Policies Tab */}
          <TabsContent value="policies">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Manage Policies</CardTitle>
                    <CardDescription>Add, edit, or remove insurance policies</CardDescription>
                  </div>
                  <Button onClick={() => setPolicyDialog({ open: true, mode: 'create', data: null })} data-testid="add-policy-button">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Policy
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Provider</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Premium</TableHead>
                        <TableHead>Coverage</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {policies.map((policy) => (
                        <TableRow key={policy.id} data-testid={`policy-row-${policy.id}`}>
                          <TableCell className="font-medium">{policy.name}</TableCell>
                          <TableCell>{policy.provider_name}</TableCell>
                          <TableCell>{policy.category_type}</TableCell>
                          <TableCell>{formatCurrency(policy.premium_yearly)}</TableCell>
                          <TableCell>{formatCurrency(policy.coverage_amount)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setPolicyDialog({ open: true, mode: 'edit', data: policy })}
                                data-testid={`edit-policy-${policy.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeletePolicy(policy.id)}
                                data-testid={`delete-policy-${policy.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Manage Categories</CardTitle>
                    <CardDescription>Add, edit, or remove insurance categories</CardDescription>
                  </div>
                  <Button onClick={() => setCategoryDialog({ open: true, mode: 'create', data: null })} data-testid="add-category-button">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Category
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((category) => (
                      <TableRow key={category.id} data-testid={`category-row-${category.id}`}>
                        <TableCell className="font-medium">{category.name}</TableCell>
                        <TableCell>{category.type}</TableCell>
                        <TableCell>{category.description || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setCategoryDialog({ open: true, mode: 'edit', data: category })}
                              data-testid={`edit-category-${category.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteCategory(category.id)}
                              data-testid={`delete-category-${category.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Manage Providers</CardTitle>
                    <CardDescription>Add, edit, or remove insurance providers</CardDescription>
                  </div>
                  <Button onClick={() => setProviderDialog({ open: true, mode: 'create', data: null })} data-testid="add-provider-button">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Provider
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {providers.map((provider) => (
                      <TableRow key={provider.id} data-testid={`provider-row-${provider.id}`}>
                        <TableCell className="font-medium">{provider.name}</TableCell>
                        <TableCell>{provider.description || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setProviderDialog({ open: true, mode: 'edit', data: provider })}
                              data-testid={`edit-provider-${provider.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteProvider(provider.id)}
                              data-testid={`delete-provider-${provider.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pages Tab */}
          <TabsContent value="pages">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Manage Pages</CardTitle>
                    <CardDescription>Create and manage custom pages for your website</CardDescription>
                  </div>
                  <Button onClick={() => setPageDialog({ open: true, mode: 'create', data: null })} data-testid="add-page-button">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Page
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>URL Slug</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last Updated</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customPages.map((page) => (
                        <TableRow key={page.id} data-testid={`page-row-${page.id}`}>
                          <TableCell className="font-medium">{page.title}</TableCell>
                          <TableCell>
                            <code className="text-xs bg-secondary-100 px-2 py-1 rounded">/{page.url_slug}</code>
                          </TableCell>
                          <TableCell>
                            <Badge variant={page.is_published ? 'default' : 'secondary'}>
                              {page.is_published ? 'Published' : 'Draft'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-secondary-600">
                            {new Date(page.updated_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setPageDialog({ open: true, mode: 'edit', data: page })}
                                data-testid={`edit-page-${page.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeletePage(page.id)}
                                data-testid={`delete-page-${page.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {customPages.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-secondary-500">
                            No custom pages yet. Click "Add Page" to create your first page.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lead Enquiries Tab */}
          <TabsContent value="leads">
            <Card>
              <CardHeader>
                <CardTitle>Lead Enquiries</CardTitle>
                <CardDescription>View and manage customer enquiries</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Full Name</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Insured</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.map((lead) => (
                        <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                          <TableCell className="font-medium">{lead.full_name}</TableCell>
                          <TableCell>{lead.country_code} {lead.mobile_number}</TableCell>
                          <TableCell>{lead.email}</TableCell>
                          <TableCell><Badge variant="secondary">{lead.category}</Badge></TableCell>
                          <TableCell>{lead.insured_person}</TableCell>
                          <TableCell className="text-sm">{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Select value={lead.status} onValueChange={(value) => handleUpdateLeadStatus(lead.id, value)}>
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="New">New</SelectItem>
                                <SelectItem value="Contacted">Contacted</SelectItem>
                                <SelectItem value="Converted">Converted</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                      {leads.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-secondary-500">
                            No lead enquiries yet. Leads will appear here when users submit the Get Quote form.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Users Management</CardTitle>
                    <CardDescription>View and manage registered users</CardDescription>
                  </div>
                  <Button onClick={() => setUserDialog({ open: true, mode: 'create', data: null })} data-testid="add-user-button">
                    <Plus className="mr-2 h-4 w-4" />
                    Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Registered</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                          <TableCell className="font-medium">{user.name || '-'}</TableCell>
                          <TableCell>{user.email || '-'}</TableCell>
                          <TableCell>{user.phone}</TableCell>
                          <TableCell>
                            <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                              {user.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.is_blocked ? 'destructive' : 'default'} className={user.is_blocked ? '' : 'bg-green-500'}>
                              {user.is_blocked ? 'Blocked' : 'Active'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{new Date(user.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setUserDialog({ open: true, mode: 'edit', data: user })}
                                data-testid={`edit-user-${user.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleUserBlock(user.id, user.is_blocked)}
                                className={user.is_blocked ? 'text-green-600 hover:bg-green-50' : 'text-orange-600 hover:bg-orange-50'}
                                data-testid={`block-user-${user.id}`}
                              >
                                {user.is_blocked ? 'Unblock' : 'Block'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteUser(user.id)}
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {users.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-secondary-500">
                            No users registered yet.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-xs text-blue-900">
                    <div><strong>Total Users:</strong> {users.length}</div>
                    <div><strong>Active:</strong> {users.filter(u => !u.is_blocked).length}</div>
                    <div><strong>Blocked:</strong> {users.filter(u => u.is_blocked).length}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Logo Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Logo Settings</CardTitle>
                  <CardDescription>Set your company logo using an external image URL</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Current Logo Preview */}
                  <div className="flex items-center gap-4 p-4 bg-secondary-50 rounded-lg border border-secondary-200">
                    <div className="flex-shrink-0">
                      {settings.logo_url ? (
                        <img 
                          src={settings.logo_url} 
                          alt="Current Logo" 
                          className="h-20 w-20 object-contain bg-white p-2 rounded-lg border border-secondary-300"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/logo.png';
                          }}
                        />
                      ) : (
                        <div className="h-20 w-20 bg-secondary-200 rounded-lg flex items-center justify-center border border-secondary-300">
                          <Shield className="h-10 w-10 text-secondary-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-secondary-900">Current Logo</p>
                      <p className="text-sm text-secondary-600">Displayed across all pages</p>
                      <p className="text-xs text-secondary-500 mt-1 break-all">
                        {settings.logo_url ? `URL: ${settings.logo_url}` : 'No logo set'}
                      </p>
                    </div>
                  </div>

                  {/* Logo URL Input */}
                  <div className="space-y-2">
                    <Label htmlFor="logo-url">Logo Image URL</Label>
                    <div className="flex gap-2">
                      <Input
                        type="url"
                        id="logo-url"
                        placeholder="https://example.com/your-logo.png"
                        defaultValue={settings.logo_url || ''}
                        data-testid="logo-url-input"
                        className="flex-1"
                      />
                      <Button 
                        onClick={async () => {
                          const input = document.getElementById('logo-url');
                          const url = input.value.trim();
                          if (!url) {
                            toast.error('Please enter a logo URL');
                            return;
                          }
                          try {
                            setUploadingLogo(true);
                            await updateSettings({ logo_url: url });
                            await refreshSettings();
                            toast.success('Logo URL updated successfully!');
                          } catch (error) {
                            toast.error('Failed to update logo URL');
                          } finally {
                            setUploadingLogo(false);
                          }
                        }}
                        disabled={uploadingLogo}
                        data-testid="save-logo-url-btn"
                      >
                        {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                    <p className="text-xs text-secondary-500">
                      Use an external image URL (PNG, JPG, SVG). Host your logo on a CDN, Google Drive (public), or image hosting service.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Company Name Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Company Name</CardTitle>
                  <CardDescription>Update your company name</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="company-name">Company Name</Label>
                    <Input
                      id="company-name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Enter company name"
                      data-testid="company-name-input"
                    />
                  </div>
                  <Button 
                    onClick={handleUpdateCompanyName}
                    disabled={submitting || companyName === settings.company_name}
                    data-testid="update-company-name-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      'Update Company Name'
                    )}
                  </Button>
                  <p className="text-xs text-secondary-500">
                    Changes will be reflected across all pages immediately
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Page Builder Tab */}
          <TabsContent value="pagebuilder">
            <div className="space-y-6">
              {/* Hero Section Editor */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Hero Section</CardTitle>
                      <CardDescription>Customize your homepage hero banner</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="hero-title">Hero Title</Label>
                      <Input
                        id="hero-title"
                        value={pageContent.hero_title}
                        onChange={(e) => setPageContent(prev => ({ ...prev, hero_title: e.target.value }))}
                        placeholder="Main headline"
                        data-testid="hero-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hero-cta">Call-to-Action Button Text</Label>
                      <Input
                        id="hero-cta"
                        value={pageContent.hero_cta_text}
                        onChange={(e) => setPageContent(prev => ({ ...prev, hero_cta_text: e.target.value }))}
                        placeholder="Button text"
                        data-testid="hero-cta-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hero-subtitle">Hero Subtitle</Label>
                    <Textarea
                      id="hero-subtitle"
                      value={pageContent.hero_subtitle}
                      onChange={(e) => setPageContent(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                      placeholder="Supporting description"
                      rows={3}
                      data-testid="hero-subtitle-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hero-image">Hero Image</Label>
                    <div className="space-y-4">
                      {/* Recommendations */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-blue-900 mb-1">Recommended Specifications:</p>
                        <ul className="text-xs text-blue-800 space-y-1">
                          <li>• <strong>Dimensions:</strong> 1200x800 pixels (or 3:2 aspect ratio)</li>
                          <li>• <strong>File Size:</strong> Maximum 5MB (2-3MB recommended for faster loading)</li>
                          <li>• <strong>Format:</strong> JPG or PNG (JPG preferred for photos)</li>
                          <li>• <strong>Content:</strong> Professional images related to insurance, family, or security</li>
                        </ul>
                      </div>

                      {/* Preview */}
                      <div className="p-4 bg-secondary-50 rounded-lg border border-secondary-200">
                        <p className="text-xs font-medium text-secondary-700 mb-2">Current Preview:</p>
                        {pageContent.hero_image_url ? (
                          <img 
                            src={resolveImageUrl(pageContent.hero_image_url)} 
                            alt="Hero preview" 
                            className="w-full h-48 object-cover rounded-lg border-2 border-secondary-300"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://images.unsplash.com/photo-1696861273647-92dfe8bb697c?w=600';
                            }}
                          />
                        ) : (
                          <div className="w-full h-48 bg-secondary-200 rounded-lg flex items-center justify-center">
                            <p className="text-secondary-500">No image set</p>
                          </div>
                        )}
                        <p className="text-xs text-secondary-500 mt-2 break-all">
                          Path: {pageContent.hero_image_url || 'Not set'}
                        </p>
                      </div>
                      
                      {/* Upload or URL */}
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-secondary-600 mb-1 block">Upload New Image</Label>
                          <Input
                            type="file"
                            id="hero-image"
                            accept="image/*"
                            onChange={handleHeroImageUpload}
                            disabled={uploadingLogo}
                            data-testid="hero-image-input"
                          />
                          {uploadingLogo && (
                            <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Uploading image...
                            </p>
                          )}
                          <p className="text-xs text-secondary-500 mt-1">Supported: PNG, JPG, JPEG, SVG (max 5MB)</p>
                        </div>
                        <div>
                          <Label className="text-xs text-secondary-600 mb-1 block">Or Enter Image URL</Label>
                          <Input
                            value={pageContent.hero_image_url || ''}
                            onChange={(e) => setPageContent(prev => ({ ...prev, hero_image_url: e.target.value }))}
                            placeholder="https://images.unsplash.com/photo-..."
                            data-testid="hero-image-url-input"
                          />
                          <p className="text-xs text-secondary-500 mt-1">Preview updates as you type</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Banner Slides Manager */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Banner Slides Manager</CardTitle>
                      <CardDescription>Manage promotional banner slides below header (auto-rotates every 5 seconds)</CardDescription>
                    </div>
                    <Button onClick={() => setSlideDialog({ open: true, mode: 'create', data: null })} size="sm">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Slide
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {bannerSlides.map((slide) => (
                      <div key={slide.id} className="p-4 border-2 border-secondary-200 rounded-lg space-y-3 bg-white">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            <img src={resolveImageUrl(slide.image_url)} alt={slide.title} className="w-40 h-24 object-cover rounded border-2 border-secondary-300" />
                            <div className="flex-1">
                              <p className="font-bold text-secondary-900 text-base mb-1">{slide.title}</p>
                              {slide.subtitle && <p className="text-sm text-secondary-600 mb-2">{slide.subtitle}</p>}
                              {slide.cta_text && (
                                <div className="flex items-center gap-2 text-xs">
                                  <Badge variant="secondary">CTA: {slide.cta_text}</Badge>
                                  <span className="text-primary-600">→ {slide.cta_link}</span>
                                </div>
                              )}
                              <div className="mt-2">
                                <Badge variant={slide.is_active ? 'default' : 'secondary'}>{slide.is_active ? 'Active' : 'Inactive'}</Badge>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={() => setSlideDialog({ open: true, mode: 'edit', data: slide })}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteBannerSlide(slide.id)} className="text-red-600 hover:bg-red-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {bannerSlides.length === 0 && (
                      <p className="text-center py-8 text-secondary-500">No banner slides yet. Click "Add Slide" to create your first promotional banner.</p>
                    )}
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs font-semibold text-blue-900 mb-2">Slider Info:</p>
                    <div className="text-xs text-blue-800 space-y-1">
                      <p>• Total Slides: {bannerSlides.length} | Active: {bannerSlides.filter(s => s.is_active).length}</p>
                      <p>• Recommended Image Size: 1920x500 pixels</p>
                      <p>• Auto-rotates: Every 5 seconds</p>
                      <p>• Supports: Title, Subtitle, CTA Button, Background Image</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stats Section Editor */}
              <Card>
                <CardHeader>
                  <CardTitle>Trust Statistics</CardTitle>
                  <CardDescription>Edit the 4 statistics displayed below the hero</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pageContent.stats.map((stat, index) => (
                      <div key={index} className="p-4 border border-secondary-200 rounded-lg space-y-3">
                        <p className="font-semibold text-sm text-secondary-700">Stat {index + 1}</p>
                        <div className="space-y-2">
                          <Label htmlFor={`stat-value-${index}`}>Value</Label>
                          <Input
                            id={`stat-value-${index}`}
                            value={stat.value}
                            onChange={(e) => handleStatChange(index, 'value', e.target.value)}
                            placeholder="e.g., 10,000+"
                            data-testid={`stat-value-${index}`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`stat-label-${index}`}>Label</Label>
                          <Input
                            id={`stat-label-${index}`}
                            value={stat.label}
                            onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                            placeholder="e.g., Happy Customers"
                            data-testid={`stat-label-${index}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Footer Content Editor */}
              <Card>
                <CardHeader>
                  <CardTitle>Footer Description</CardTitle>
                  <CardDescription>Update footer description text</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="footer-desc">Footer Description</Label>
                    <Textarea
                      id="footer-desc"
                      value={pageContent.footer_description}
                      onChange={(e) => setPageContent(prev => ({ ...prev, footer_description: e.target.value }))}
                      placeholder="Brief description about your company"
                      rows={4}
                      data-testid="footer-description-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Footer Contact Editor */}
              <Card>
                <CardHeader>
                  <CardTitle>Footer Contact Information</CardTitle>
                  <CardDescription>Update contact details displayed in footer</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="footer-email">Email Address</Label>
                      <Input
                        id="footer-email"
                        value={pageContent.footer_email}
                        onChange={(e) => setPageContent(prev => ({ ...prev, footer_email: e.target.value }))}
                        placeholder="support@nobleshield.com"
                        data-testid="footer-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="footer-phone">Phone Number</Label>
                      <Input
                        id="footer-phone"
                        value={pageContent.footer_phone}
                        onChange={(e) => setPageContent(prev => ({ ...prev, footer_phone: e.target.value }))}
                        placeholder="+91 755 1196 989"
                        data-testid="footer-phone-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Footer Columns Builder */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Footer Link Columns</CardTitle>
                      <CardDescription>Create footer columns with links (like Products, Company, Resources)</CardDescription>
                    </div>
                    <Button onClick={handleAddFooterColumn} size="sm" data-testid="add-footer-column-button">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Column
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {pageContent.footer_columns.map((column, columnIndex) => (
                      <div key={columnIndex} className="p-4 border-2 border-secondary-200 rounded-lg space-y-4 bg-secondary-50">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-secondary-900">Column {columnIndex + 1}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFooterColumn(columnIndex)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Column Title</Label>
                          <Input
                            value={column.title}
                            onChange={(e) => handleFooterColumnChange(columnIndex, 'title', e.target.value)}
                            placeholder="e.g., Products, Company, Resources"
                            data-testid={`footer-column-title-${columnIndex}`}
                          />
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label>Links</Label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddLink(columnIndex)}
                              data-testid={`add-link-${columnIndex}`}
                            >
                              <Plus className="mr-1 h-3 w-3" />
                              Add Link
                            </Button>
                          </div>
                          
                          {column.links && column.links.map((link, linkIndex) => (
                            <div key={linkIndex} className="flex items-center gap-2 p-3 bg-white rounded border border-secondary-200">
                              <div className="flex-1 grid grid-cols-2 gap-2">
                                <Input
                                  value={link.text}
                                  onChange={(e) => handleLinkChange(columnIndex, linkIndex, 'text', e.target.value)}
                                  placeholder="Link text"
                                  size="sm"
                                  data-testid={`link-text-${columnIndex}-${linkIndex}`}
                                />
                                <Input
                                  value={link.url}
                                  onChange={(e) => handleLinkChange(columnIndex, linkIndex, 'url', e.target.value)}
                                  placeholder="URL"
                                  size="sm"
                                  data-testid={`link-url-${columnIndex}-${linkIndex}`}
                                />
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveLink(columnIndex, linkIndex)}
                                className="h-8 w-8"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    {pageContent.footer_columns.length === 0 && (
                      <div className="text-center py-8 text-secondary-500">
                        <p className="mb-2">No footer columns yet. Click "Add Column" to create your first footer section.</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Social Media Links Builder */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Social Media Links</CardTitle>
                      <CardDescription>Add social media profiles to footer</CardDescription>
                    </div>
                    <Button onClick={handleAddSocialMedia} size="sm" data-testid="add-social-button">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Social Link
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pageContent.social_media.map((social, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 bg-secondary-50 rounded border border-secondary-200">
                        <Select
                          value={social.platform}
                          onValueChange={(value) => handleSocialMediaChange(index, 'platform', value)}
                        >
                          <SelectTrigger className="w-40" data-testid={`social-platform-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Facebook">Facebook</SelectItem>
                            <SelectItem value="Twitter">Twitter</SelectItem>
                            <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                            <SelectItem value="Instagram">Instagram</SelectItem>
                            <SelectItem value="YouTube">YouTube</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          value={social.url}
                          onChange={(e) => handleSocialMediaChange(index, 'url', e.target.value)}
                          placeholder="https://..."
                          className="flex-1"
                          data-testid={`social-url-${index}`}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveSocialMedia(index)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    ))}
                    
                    {pageContent.social_media.length === 0 && (
                      <div className="text-center py-4 text-secondary-500">
                        <p>No social media links yet. Click "Add Social Link" to get started.</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Regulatory & Compliance Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Regulatory & Compliance Information</CardTitle>
                  <CardDescription>Add company legal and regulatory details for footer</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company-full-name">Full Company Name</Label>
                      <Input
                        id="company-full-name"
                        value={pageContent.company_full_name}
                        onChange={(e) => setPageContent(prev => ({ ...prev, company_full_name: e.target.value }))}
                        placeholder="Noble Shield Insurance Brokers LLP"
                        data-testid="company-full-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="irdai">IRDAI License Number</Label>
                      <Input
                        id="irdai"
                        value={pageContent.irdai_license}
                        onChange={(e) => setPageContent(prev => ({ ...prev, irdai_license: e.target.value }))}
                        placeholder="1107"
                        data-testid="irdai-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="broker-type">Broker Type</Label>
                      <Input
                        id="broker-type"
                        value={pageContent.broker_type}
                        onChange={(e) => setPageContent(prev => ({ ...prev, broker_type: e.target.value }))}
                        placeholder="Composite Broker"
                        data-testid="broker-type-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="license-valid">License Valid Till</Label>
                      <Input
                        id="license-valid"
                        value={pageContent.license_valid_till}
                        onChange={(e) => setPageContent(prev => ({ ...prev, license_valid_till: e.target.value }))}
                        placeholder="10/11/2028"
                        data-testid="license-valid-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="principal-officer">Principal Officer</Label>
                      <Input
                        id="principal-officer"
                        value={pageContent.principal_officer}
                        onChange={(e) => setPageContent(prev => ({ ...prev, principal_officer: e.target.value }))}
                        placeholder="Mr. Raj"
                        data-testid="principal-officer-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cin">CIN (Corporate Identification Number)</Label>
                      <Input
                        id="cin"
                        value={pageContent.cin}
                        onChange={(e) => setPageContent(prev => ({ ...prev, cin: e.target.value }))}
                        placeholder="Optional"
                        data-testid="cin-input"
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="disclaimer">Disclaimer Text</Label>
                      <Textarea
                        id="disclaimer"
                        value={pageContent.disclaimer_text}
                        onChange={(e) => setPageContent(prev => ({ ...prev, disclaimer_text: e.target.value }))}
                        placeholder="*Standard T&C Apply..."
                        rows={3}
                        data-testid="disclaimer-input"
                      />
                      <p className="text-xs text-secondary-500">Short disclaimer for regulatory section</p>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="footer-disclaimer-full">Full Footer Disclaimer</Label>
                      <Textarea
                        id="footer-disclaimer-full"
                        value={pageContent.footer_disclaimer_full}
                        onChange={(e) => setPageContent(prev => ({ ...prev, footer_disclaimer_full: e.target.value }))}
                        placeholder="*Standard T&C Apply. All savings/offers are offered by insurance companies..."
                        rows={5}
                        data-testid="footer-disclaimer-full-input"
                      />
                      <p className="text-xs text-secondary-500">Comprehensive disclaimer below footer (about IRDAI, risk factors, etc.)</p>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="ndnc-auth">NDNC Authorization Text</Label>
                      <Textarea
                        id="ndnc-auth"
                        value={pageContent.ndnc_authorization}
                        onChange={(e) => setPageContent(prev => ({ ...prev, ndnc_authorization: e.target.value }))}
                        placeholder="You authorise Noble Shield to contact you through call, SMS, email..."
                        rows={2}
                        data-testid="ndnc-authorization-input"
                      />
                      <p className="text-xs text-secondary-500">Authorization for communication (NDNC override)</p>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="registered-office">Registered Office Address</Label>
                      <Textarea
                        id="registered-office"
                        value={pageContent.registered_office}
                        onChange={(e) => setPageContent(prev => ({ ...prev, registered_office: e.target.value }))}
                        placeholder="Full registered office address"
                        rows={2}
                        data-testid="registered-office-input"
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="corporate-office">Corporate Office Address</Label>
                      <Textarea
                        id="corporate-office"
                        value={pageContent.corporate_office}
                        onChange={(e) => setPageContent(prev => ({ ...prev, corporate_office: e.target.value }))}
                        placeholder="Full corporate office address"
                        rows={2}
                        data-testid="corporate-office-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Save All Changes Button */}
              <div className="flex justify-end">
                <Button 
                  size="lg"
                  onClick={handleUpdatePageContent}
                  disabled={submitting}
                  data-testid="save-page-changes-button"
                  className="min-w-[200px]"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Saving Changes...
                    </>
                  ) : (
                    'Save All Changes'
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Policy Dialog */}
      <Dialog open={policyDialog.open} onOpenChange={(open) => setPolicyDialog({ ...policyDialog, open })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="policy-dialog">
          <DialogHeader>
            <DialogTitle>{policyDialog.mode === 'create' ? 'Add New Policy' : 'Edit Policy'}</DialogTitle>
            <DialogDescription>
              {policyDialog.mode === 'create' ? 'Create a new insurance policy' : 'Update policy information'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePolicySubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Policy Name *</Label>
                <Input id="name" name="name" defaultValue={policyDialog.data?.name} required data-testid="policy-name-input" />
              </div>
              <div>
                <Label htmlFor="provider_id">Provider *</Label>
                <Select name="provider_id" defaultValue={policyDialog.data?.provider_id} required>
                  <SelectTrigger data-testid="policy-provider-select">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {providers.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>{provider.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="category_id">Category *</Label>
                <Select name="category_id" defaultValue={policyDialog.data?.category_id} required>
                  <SelectTrigger data-testid="policy-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="premium_yearly">Annual Premium *</Label>
                <Input id="premium_yearly" name="premium_yearly" type="number" defaultValue={policyDialog.data?.premium_yearly} required data-testid="policy-premium-input" />
              </div>
              <div>
                <Label htmlFor="coverage_amount">Coverage Amount *</Label>
                <Input id="coverage_amount" name="coverage_amount" type="number" defaultValue={policyDialog.data?.coverage_amount} required data-testid="policy-coverage-input" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="summary">Summary *</Label>
                <Textarea id="summary" name="summary" defaultValue={policyDialog.data?.summary} required rows={3} data-testid="policy-summary-input" />
              </div>
              <div className="flex items-center space-x-2">
                <Switch id="cashless" name="cashless" defaultChecked={policyDialog.data?.features?.cashless} data-testid="policy-cashless-switch" />
                <Label htmlFor="cashless">Cashless Facility</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch id="pre_existing" name="pre_existing" defaultChecked={policyDialog.data?.features?.pre_existing} data-testid="policy-preexisting-switch" />
                <Label htmlFor="pre_existing">Pre-existing Cover</Label>
              </div>
              <div>
                <Label htmlFor="network_size">Network Size</Label>
                <Input id="network_size" name="network_size" type="number" defaultValue={policyDialog.data?.features?.network_size} data-testid="policy-network-input" />
              </div>
              <div>
                <Label htmlFor="claim_settlement_ratio">Claim Settlement Ratio (%)</Label>
                <Input id="claim_settlement_ratio" name="claim_settlement_ratio" type="number" step="0.1" defaultValue={policyDialog.data?.features?.claim_settlement_ratio} data-testid="policy-claim-ratio-input" />
              </div>
              <div>
                <Label htmlFor="waiting_period_days">Waiting Period (days)</Label>
                <Input id="waiting_period_days" name="waiting_period_days" type="number" defaultValue={policyDialog.data?.features?.waiting_period_days} data-testid="policy-waiting-input" />
              </div>
              <div className="flex items-center space-x-2">
                <Switch id="is_popular" name="is_popular" defaultChecked={policyDialog.data?.is_popular} data-testid="policy-popular-switch" />
                <Label htmlFor="is_popular">Mark as Popular</Label>
              </div>
              <div className="col-span-2">
                <Label htmlFor="key_benefits">Key Benefits (one per line)</Label>
                <Textarea id="key_benefits" name="key_benefits" defaultValue={policyDialog.data?.key_benefits?.join('\n')} rows={4} data-testid="policy-benefits-input" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="exclusions">Exclusions (one per line)</Label>
                <Textarea id="exclusions" name="exclusions" defaultValue={policyDialog.data?.exclusions?.join('\n')} rows={4} data-testid="policy-exclusions-input" />
              </div>
              <div className="col-span-2">
                <Label htmlFor="terms">Terms & Conditions</Label>
                <Textarea id="terms" name="terms" defaultValue={policyDialog.data?.terms} rows={3} data-testid="policy-terms-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPolicyDialog({ open: false, mode: 'create', data: null })} data-testid="cancel-policy-button">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-policy-button">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : policyDialog.mode === 'create' ? 'Create Policy' : 'Update Policy'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialog.open} onOpenChange={(open) => setCategoryDialog({ ...categoryDialog, open })}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{categoryDialog.mode === 'create' ? 'Add New Category' : 'Edit Category'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div>
              <Label htmlFor="cat-name">Name *</Label>
              <Input id="cat-name" name="name" defaultValue={categoryDialog.data?.name} required data-testid="category-name-input" />
            </div>
            <div>
              <Label htmlFor="type">Type *</Label>
              <Select name="type" defaultValue={categoryDialog.data?.type} required>
                <SelectTrigger data-testid="category-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Health">Health</SelectItem>
                  <SelectItem value="Life">Life</SelectItem>
                  <SelectItem value="Motor">Motor</SelectItem>
                  <SelectItem value="Travel">Travel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cat-description">Description</Label>
              <Textarea id="cat-description" name="description" defaultValue={categoryDialog.data?.description} rows={3} data-testid="category-description-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCategoryDialog({ open: false, mode: 'create', data: null })} data-testid="cancel-category-button">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-category-button">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : categoryDialog.mode === 'create' ? 'Create' : 'Update'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Provider Dialog */}
      <Dialog open={providerDialog.open} onOpenChange={(open) => setProviderDialog({ ...providerDialog, open })}>
        <DialogContent data-testid="provider-dialog">
          <DialogHeader>
            <DialogTitle>{providerDialog.mode === 'create' ? 'Add New Provider' : 'Edit Provider'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleProviderSubmit} className="space-y-4">
            <div>
              <Label htmlFor="prov-name">Name *</Label>
              <Input id="prov-name" name="name" defaultValue={providerDialog.data?.name} required data-testid="provider-name-input" />
            </div>
            <div>
              <Label htmlFor="logo_url">Logo URL</Label>
              <Input id="logo_url" name="logo_url" type="url" defaultValue={providerDialog.data?.logo_url} data-testid="provider-logo-input" />
            </div>
            <div>
              <Label htmlFor="prov-description">Description</Label>
              <Textarea id="prov-description" name="description" defaultValue={providerDialog.data?.description} rows={3} data-testid="provider-description-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setProviderDialog({ open: false, mode: 'create', data: null })} data-testid="cancel-provider-button">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-provider-button">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : providerDialog.mode === 'create' ? 'Create' : 'Update'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Custom Page Dialog */}
      <Dialog open={pageDialog.open} onOpenChange={(open) => setPageDialog({ ...pageDialog, open })}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="page-dialog">
          <DialogHeader>
            <DialogTitle>{pageDialog.mode === 'create' ? 'Create New Page' : 'Edit Page'}</DialogTitle>
            <DialogDescription>
              {pageDialog.mode === 'create' ? 'Create a custom page with SEO optimization' : 'Update page content and SEO settings'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePageSubmit} className="space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="page-title">Page Title *</Label>
                    <Input 
                      id="page-title" 
                      name="title" 
                      defaultValue={pageDialog.data?.title} 
                      required 
                      placeholder="About Us" 
                      data-testid="page-title-input" 
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="url-slug">URL Slug *</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-secondary-600">/page/</span>
                      <Input 
                        id="url-slug" 
                        name="url_slug" 
                        defaultValue={pageDialog.data?.url_slug} 
                        required 
                        placeholder="about-us"
                        pattern="[a-z0-9-]+"
                        title="Only lowercase letters, numbers, and hyphens allowed"
                        data-testid="url-slug-input"
                        onChange={(e) => {
                          const slug = e.target.value;
                          const preview = document.getElementById('url-preview');
                          if (preview) {
                            preview.textContent = `https://insure-scout-1.preview.emergentagent.com/page/${slug}`;
                          }
                        }}
                      />
                    </div>
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-xs text-secondary-500 mb-1">Complete URL:</p>
                      <p 
                        id="url-preview" 
                        className="text-sm font-mono text-primary-700 break-all"
                        data-testid="url-preview"
                      >
                        https://insure-scout-1.preview.emergentagent.com/page/{pageDialog.data?.url_slug || 'your-page-slug'}
                      </p>
                    </div>
                    <p className="text-xs text-secondary-500 mt-1">
                      Clean, SEO-friendly URL. Use lowercase letters, numbers, and hyphens only.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Page Content */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Page Content</CardTitle>
                <CardDescription>Use the visual editor to create your content - no HTML knowledge required!</CardDescription>
              </CardHeader>
              <CardContent>
                <TipTapEditor
                  content={pageEditorContent}
                  onChange={setPageEditorContent}
                />
                <p className="text-xs text-secondary-500 mt-3">
                  Use the toolbar buttons above to format text, add headings, lists, links, and images. Click the image icon to upload images directly.
                </p>
              </CardContent>
            </Card>

            {/* SEO Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary-600" />
                  SEO Optimization
                </CardTitle>
                <CardDescription>Configure search engine optimization</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="meta-desc">Meta Description (SEO)</Label>
                    <Textarea 
                      id="meta-desc" 
                      name="meta_description" 
                      defaultValue={pageDialog.data?.meta_description} 
                      placeholder="Brief description for search engines (150-160 characters recommended)" 
                      rows={2}
                      maxLength={160}
                      data-testid="meta-description-input" 
                    />
                    <p className="text-xs text-secondary-500 mt-1">Appears in search results</p>
                  </div>
                  <div>
                    <Label htmlFor="meta-keywords">Meta Keywords</Label>
                    <Input 
                      id="meta-keywords" 
                      name="meta_keywords" 
                      defaultValue={pageDialog.data?.meta_keywords} 
                      placeholder="insurance, health insurance, policy comparison"
                      data-testid="meta-keywords-input" 
                    />
                    <p className="text-xs text-secondary-500 mt-1">Comma-separated keywords</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="og-title">Open Graph Title</Label>
                      <Input 
                        id="og-title" 
                        name="og_title" 
                        defaultValue={pageDialog.data?.og_title} 
                        placeholder="Title for social media sharing"
                        data-testid="og-title-input" 
                      />
                    </div>
                    <div>
                      <Label htmlFor="og-image">Open Graph Image URL</Label>
                      <Input 
                        id="og-image" 
                        name="og_image" 
                        defaultValue={pageDialog.data?.og_image} 
                        placeholder="https://..."
                        data-testid="og-image-input" 
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="og-desc">Open Graph Description</Label>
                    <Textarea 
                      id="og-desc" 
                      name="og_description" 
                      defaultValue={pageDialog.data?.og_description} 
                      placeholder="Description when shared on social media"
                      rows={2}
                      data-testid="og-description-input" 
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="robots">Search Engine Indexing</Label>
                      <Select name="robots" defaultValue={pageDialog.data?.robots || "index, follow"}>
                        <SelectTrigger data-testid="robots-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="index, follow">Index & Follow (Default)</SelectItem>
                          <SelectItem value="noindex, follow">No Index, Follow</SelectItem>
                          <SelectItem value="index, nofollow">Index, No Follow</SelectItem>
                          <SelectItem value="noindex, nofollow">No Index, No Follow</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="canonical">Canonical URL</Label>
                      <Input 
                        id="canonical" 
                        name="canonical_url" 
                        defaultValue={pageDialog.data?.canonical_url} 
                        placeholder="https://... (optional)"
                        data-testid="canonical-input" 
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Publish Setting */}
            <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg border border-secondary-200">
              <div className="flex items-center space-x-3">
                <Switch 
                  id="is-published" 
                  name="is_published" 
                  defaultChecked={pageDialog.data?.is_published !== false} 
                  data-testid="page-published-switch" 
                />
                <div>
                  <Label htmlFor="is-published" className="cursor-pointer font-semibold">
                    Publish this page
                  </Label>
                  <p className="text-xs text-secondary-600">Make page visible to users</p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPageDialog({ open: false, mode: 'create', data: null })} data-testid="cancel-page-button">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-page-button">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : pageDialog.mode === 'create' ? 'Create Page' : 'Update Page'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Banner Slide Dialog */}
      <Dialog open={slideDialog.open} onOpenChange={(open) => setSlideDialog({ ...slideDialog, open })}>
        <DialogContent className="max-w-3xl" data-testid="slide-dialog">
          <DialogHeader>
            <DialogTitle>{slideDialog.mode === 'create' ? 'Add Banner Slide' : 'Edit Banner Slide'}</DialogTitle>
            <DialogDescription>Create promotional banners for homepage slider</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSlideSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="slide-title">Slide Title *</Label>
              <Input id="slide-title" name="title" defaultValue={slideDialog.data?.title} required placeholder="Protect Your Family with Health Insurance" data-testid="slide-title-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slide-subtitle">Subtitle</Label>
              <Input id="slide-subtitle" name="subtitle" defaultValue={slideDialog.data?.subtitle} placeholder="Get comprehensive coverage starting at ₹500/month" data-testid="slide-subtitle-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slide-image">Image URL *</Label>
              <Input id="slide-image" name="image_url" defaultValue={slideDialog.data?.image_url} required placeholder="https://images.unsplash.com/photo-..." data-testid="slide-image-input" />
              <p className="text-xs text-secondary-500">Recommended: 1920x500 pixels, JPG format</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="slide-cta-text">CTA Button Text</Label>
                <Input id="slide-cta-text" name="cta_text" defaultValue={slideDialog.data?.cta_text} placeholder="Get Quote" data-testid="slide-cta-text-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slide-cta-link">CTA Button Link</Label>
                <Input id="slide-cta-link" name="cta_link" defaultValue={slideDialog.data?.cta_link} placeholder="/get-quote" data-testid="slide-cta-link-input" />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch id="slide-active" name="is_active" defaultChecked={slideDialog.data?.is_active !== false} data-testid="slide-active-switch" />
              <Label htmlFor="slide-active">Active (show in slider)</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSlideDialog({ open: false, mode: 'create', data: null })}>Cancel</Button>
              <Button type="submit" disabled={submitting}>{submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : slideDialog.mode === 'create' ? 'Create Slide' : 'Update Slide'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={userDialog.open} onOpenChange={(open) => setUserDialog({ ...userDialog, open })}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{userDialog.mode === 'create' ? 'Add New User' : 'Edit User'}</DialogTitle>
            <DialogDescription>
              {userDialog.mode === 'create' ? 'Create a new user account' : 'Update user details'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUserSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user-phone">Phone Number *</Label>
              <Input 
                id="user-phone" 
                name="phone"
                defaultValue={userDialog.data?.phone} 
                disabled={userDialog.mode === 'edit'}
                className={userDialog.mode === 'edit' ? 'bg-secondary-100' : ''}
                placeholder="10-digit phone number"
                required={userDialog.mode === 'create'}
              />
              {userDialog.mode === 'edit' && (
                <p className="text-xs text-secondary-500">Phone number cannot be changed</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-name">Full Name *</Label>
              <Input 
                id="user-name" 
                name="name" 
                defaultValue={userDialog.data?.name} 
                placeholder="Enter full name" 
                data-testid="user-name-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-email">Email Address *</Label>
              <Input 
                id="user-email" 
                name="email" 
                type="email" 
                defaultValue={userDialog.data?.email} 
                placeholder="user@example.com" 
                data-testid="user-email-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-role">Role *</Label>
              <Select name="role" defaultValue={userDialog.data?.role || 'user'}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-password">
                {userDialog.mode === 'create' ? 'Password *' : 'New Password (Optional)'}
              </Label>
              <Input 
                id="user-password" 
                name={userDialog.mode === 'create' ? 'password' : 'new_password'}
                type="password" 
                placeholder={userDialog.mode === 'create' ? 'Enter password' : 'Leave blank to keep current password'}
                data-testid="user-password-input"
                required={userDialog.mode === 'create'}
              />
              {userDialog.mode === 'edit' && (
                <p className="text-xs text-secondary-500">Only fill if you want to change the user's password</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUserDialog({ open: false, mode: 'edit', data: null })}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : userDialog.mode === 'create' ? 'Create User' : 'Update User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
